import { createSlice } from '@reduxjs/toolkit';
import { clearFilters } from 'config/sessionStorage';
import {
  login,
  logout,
  getUser,
  getMetadata,
  updateUser,
  checkIsLogin,
  setIsLoginToStorage,
} from '../api';
import isEqual from 'lodash/isEqual';
import { REQUEST_STATUS } from '../constants';
import { checkPermission } from 'helpers/checkPermissions';
import permissions from 'config/permissions';

const userInitialState = {
  isLogin: null,
  isLoginStatus: REQUEST_STATUS.idle,
  isLoginErrors: {},
  initPath: null,

  isMaintenanceMode: false,

  hipaaCheckParams: null,

  metadata: null,
  metadataStatus: REQUEST_STATUS.idle,

  user: null,
  status: REQUEST_STATUS.idle,
  error: null,

  blockedAccounts: [],

  isDistributionSponsor: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState: userInitialState,
  reducers: {
    updateIsMaintenanceMode: (state, action) => {
      state.isMaintenanceMode = action.payload;
    },
    getIsLogin: (state) => {
      state.isLogin = checkIsLogin() === 'true';
    },
    setIsLogin: (state, action) => {
      setIsLoginToStorage(action.payload);
      state.isLogin = action.payload;
    },
    setIsLoginStatus: (state, action) => {
      state.isLoginStatus = action.payload;
    },
    setBlockedAccounts: (state, action) => {
      state.blockedAccounts = action.payload;
    },
    setUser: (state, action) => {
      if (!isEqual(state.user, action.payload)) {
        state.user = action.payload;
      }
    },
    setHipaaCheckParams: (state, action) => {
      if (!isEqual(state.hipaaCheckParams, action.payload)) {
        state.hipaaCheckParams = action.payload;
      }
    },
    setMetadata: (state, action) => {
      const isDistributionSponsor = checkPermission(
        action.payload.currentAccountPermissions,
        permissions.ACCOUNTS_GET,
      );
      state.metadata = action.payload;
      state.isDistributionSponsor = isDistributionSponsor;
    },
    setInitPath: (state, action) => {
      state.initPath = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(login.pending, (state) => {
        state.isLoginStatus = REQUEST_STATUS.loading;
        state.isLoginErrors = {};
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoginStatus = REQUEST_STATUS.failed;
      })
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload.ok) {
          if (!action.payload.data.data.authMetadata) {
            state.isLoginStatus = REQUEST_STATUS.idle;
            return;
          }
          clearFilters();
          setIsLoginToStorage(true);
          state.isLogin = true;
          state.hipaaCheckParams = {
            newUser: action.payload.data.data.user,
            newMetadata: action.payload.data.data.authMetadata,
          };
        } else {
          state.isLoginStatus = REQUEST_STATUS.failed;
          if ((action.payload.status === 422)) {
            state.isLoginErrors = action.payload.data.validationErrors;
          }
        }
      })
      .addCase(logout.pending, (state) => {
        state.isLogoutStatus = REQUEST_STATUS.loading;
        state.isLoginErrors = {};
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLogoutStatus = REQUEST_STATUS.failed;
      })
      .addCase(logout.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.isLogin = false;
          state.isLogoutStatus = REQUEST_STATUS.succeeded;
          state.user = null;
          state.metadata = null;
          state.isDistributionSponsor = false;
          state.hipaaCheckParams = null;
          state.blockedAccounts = [];
          clearFilters();
          setIsLoginToStorage(false);
        } else {
          state.isLogoutStatus = REQUEST_STATUS.failed;
        }
      })
      .addCase(getUser.pending, (state) => {
        if (state.status === REQUEST_STATUS.idle) {
          state.status = 'idle_loading';
        } else {
          state.status = REQUEST_STATUS.loading;
        }
      })
      .addCase(getUser.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.user = action.payload.data.data;
          state.status = REQUEST_STATUS.succeeded;
        } else {
          state.status = REQUEST_STATUS.failed;
          if (action.error) {
            state.error = action.error.message;
          }
        }
      })
      .addCase(updateUser.pending, (state) => {
        state.status = REQUEST_STATUS.loading;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
        state.error = action?.error?.message;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.status = REQUEST_STATUS.succeeded;
        } else {
          state.status = REQUEST_STATUS.failed;
          state.error = action.error.message;
        }
      })
      .addCase(getMetadata.pending, (state) => {
        state.metadataStatus = REQUEST_STATUS.loading;
      })
      .addCase(getMetadata.rejected, (state, action) => {
        state.metadataStatus = REQUEST_STATUS.failed;
        state.metadata = null;
        state.isDistributionSponsor = false;
      })
      .addCase(getMetadata.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.metadataStatus = REQUEST_STATUS.succeeded;
          state.metadata = action.payload.data.data;
          const isDistributionSponsor = action?.payload?.data?.data?.currentAccountPermissions
            ? checkPermission(
              action?.payload?.data?.data?.currentAccountPermissions,
              permissions.ACCOUNTS_GET,
            )
            : false;
          state.isDistributionSponsor = isDistributionSponsor;
        } else {
          state.metadataStatus = REQUEST_STATUS.failed;
          state.metadata = null;
          state.isDistributionSponsor = false;
        }
      });
  },
});

export const {
  updateIsMaintenanceMode,
  setUser,
  setHipaaCheckParams,
  setMetadata,
  getIsLogin,
  setIsLogin,
  setIsLoginStatus,
  setBlockedAccounts,
  setInitPath,
} = userSlice.actions;

export default userSlice.reducer;
